/**
 * AI-credits display helpers + the credits<->money contract.
 *
 * Credits are an abstract, whole-number count (not money), so they format as a plain
 * thousands-separated integer, independent of the brand's billing currency. The unit word
 * ("credits") is UI copy and goes through `<Text>` / `t()` at the render site, never here.
 */

/* ---------------------------------------------------------------------------------------------
 * CREDITS <-> MONEY CONTRACT.
 *
 * AI credits are an OWNER-LEVEL virtual-currency wallet on the owner-hub wallet service
 * (wallet_type `AI_CREDITS`, currency `AIC`; 1 credit = 1 AIC). The balance is read off that
 * wallet (`state.ownerWallet.credits`). Buying credits mints a `deposit_ai_credits` invoice
 * priced in fiat at the wallet's exchange rate, payable by card or from the MAIN wallet.
 *
 * The exchange RATE (fiat per credit) is a property of the wallet, owned by the BE and fetched
 * live via `POST /invoice/deposit/query-ai-credits` (its `exchange_rate`), surfaced on the store
 * as `state.aiCredits.rateUsd`. The conversion helpers below take that rate as an argument;
 * {@link CREDIT_UNIT_USD} is only the fallback used until the live rate has loaded (never a fake 0).
 * ------------------------------------------------------------------------------------------------- */

/**
 * Fallback rate: one AI credit is worth this many USD. Used only until the live rate
 * (`state.aiCredits.rateUsd`, from the BE quote endpoint) has loaded. The BE is authoritative and
 * reprices the invoice at creation time, so this fallback only affects the pre-load preview.
 */
export const CREDIT_UNIT_USD = 0.1;

/** The money currency a credits top-up is priced + funded in (the rate is USD-denominated), so the
 *  MAIN wallet used to fund it is resolved in this currency. */
export const CREDIT_CURRENCY = 'USD';

/** Money cost (in the credit's USD unit) of buying `credits` at `rate` USD/credit. */
export const creditsToUsd = (credits: number, rate: number = CREDIT_UNIT_USD): number =>
  Math.max(0, credits) * (rate > 0 ? rate : CREDIT_UNIT_USD);

/** How many whole credits `usd` buys at `rate` USD/credit (e.g. $25 at 0.1 -> 250 credits). */
export const usdToCredits = (usd: number, rate: number = CREDIT_UNIT_USD): number =>
  Math.max(0, Math.round(usd / (rate > 0 ? rate : CREDIT_UNIT_USD)));

/** FE-driven top-up suggestions (no backend packs): round DOLLAR amounts, shown as their credit
 *  equivalent. Money-anchored — the $ is the anchor, credits are derived at the live rate. */
export const CREDIT_TOPUP_USD_PRESETS = [10, 25, 50] as const;

/** A credit balance as a thousands-separated integer (e.g. `1500` → "1,500"). Clamps >= 0. */
export const formatCredits = (n: number): string => Math.max(0, Math.round(n)).toLocaleString('en-US');
